import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { useData } from '../contexts/DataContext'
import { useLocation } from '../contexts/LocationContext'
import { useFilters } from '../contexts/FilterContext'
import { useView } from '../contexts/ViewContext'
import LocationSelector from '../components/selectors/LocationSelector'
import SecondaryNav from '../components/dashboard/SecondaryNav'
import FilterPanel from '../components/filters/FilterPanel'
import MapView from './MapView'
import GridView from './GridView'
import ChartsView from './ChartsView'
import ComparisonView from './ComparisonView'
import TrendsView from './TrendsView'
import QueryInterface from '../components/QueryInterface'
import { useFilteredFacilities } from '../hooks/useFilteredFacilities'

function Dashboard() {
  const { view } = useParams()
  const navigate = useNavigate()
  const [isQueryInterfaceOpen, setIsQueryInterfaceOpen] = useState(false)
  const {
    config,
    locations: _locations,
    attributes: _attributes,
    loading,
    loadFacilityData,
    loadAttributesData,
  } = useData()
  const { selectedDistrict, setDistrict } = useLocation()
  const { activeCategory, setFilter } = useFilters()
  const { activeView, setActiveView } = useView()
  const { facilities: _facilities, total, filtered } = useFilteredFacilities()

  // Set active view from URL
  useEffect(() => {
    if (view && view !== activeView) {
      setActiveView(view)
    }
  }, [view, activeView, setActiveView])

  // Set default district
  useEffect(() => {
    if (!selectedDistrict && config?.app?.default_location) {
      setDistrict(config.app.default_location) // Default to Kayunga for now
    }
  }, [selectedDistrict, config, setDistrict])

  // Load facility data and attributes when category changes
  useEffect(() => {
    loadFacilityData(activeCategory)
    loadAttributesData(activeCategory)
  }, [activeCategory, loadFacilityData, loadAttributesData])

  // Handle applying filters from query interface
  const handleApplyFilters = filters => {
    // Apply filters from query response
    Object.entries(filters).forEach(([groupId, groupFilters]) => {
      Object.entries(groupFilters).forEach(([filterId, value]) => {
        setFilter(groupId, filterId, value)
      })
    })
  }

  // Handle view switching from query interface
  const handleSwitchView = viewName => {
    setActiveView(viewName)
    navigate(`/dashboard/${viewName}`)
  }

  if (loading.config || loading.locations) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Secondary Navigation - Category and View Selectors */}
      <SecondaryNav />

      <div className="space-y-4 mt-4">
        {/* Location Selector Panel */}
        <div className="panel p-4 bg-gradient-to-r from-white to-surface-50">
          <LocationSelector />
        </div>

        {/* Main Content with Filter Panel */}
        <div
          className={`grid grid-cols-1 gap-4 ${activeView === 'trends' ? 'lg:grid-cols-1' : 'lg:grid-cols-4'}`}
        >
          {activeView !== 'trends' && (
            <div className="lg:col-span-1">
              <FilterPanel />
            </div>
          )}

          {/* Main Content Area */}
          <div
            className={activeView === 'trends' ? 'col-span-1' : 'lg:col-span-3'}
          >
            <div className="chart-container min-h-96">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-surface-900 flex items-center">
                  <span className="text-2xl mr-3">
                    {activeView === 'map'
                      ? '🗺️'
                      : activeView === 'grid'
                        ? '📋'
                        : activeView === 'charts'
                          ? '📊'
                          : activeView === 'comparison'
                            ? '📈'
                            : activeView === 'trends'
                              ? '📈'
                              : '🔍'}
                  </span>
                  {activeView.charAt(0).toUpperCase() + activeView.slice(1)}{' '}
                  View
                </h3>
                {activeView !== 'trends' && (
                  <div className="text-sm text-surface-600 bg-surface-100 px-3 py-2 rounded-lg font-medium">
                    {filtered !== total ? (
                      <span>
                        Showing{' '}
                        <span className="font-semibold text-primary-600">
                          {filtered}
                        </span>{' '}
                        of <span className="font-semibold">{total}</span> items
                      </span>
                    ) : (
                      <span>
                        <span className="font-semibold text-primary-600">
                          {total}
                        </span>{' '}
                        items
                      </span>
                    )}
                  </div>
                )}
              </div>

              {(() => {
                if (loading[`facilities_${activeCategory}`]) {
                  return (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading data...</p>
                      </div>
                    </div>
                  )
                }

                switch (activeView) {
                  case 'map':
                    return <MapView />
                  case 'grid':
                    return <GridView />
                  case 'charts':
                    return <ChartsView />
                  case 'comparison':
                    return <ComparisonView />
                  case 'trends':
                    return <TrendsView />
                  default:
                    return (
                      <div className="flex items-center justify-center h-64 text-gray-500">
                        <div className="text-center">
                          <div className="text-4xl mb-2">🚧</div>
                          <p>View not found</p>
                        </div>
                      </div>
                    )
                }
              })()}
            </div>
          </div>
        </div>

        {/* Floating AI Query Button */}
        <button
          onClick={() => setIsQueryInterfaceOpen(true)}
          className="fixed bottom-6 right-6 p-4 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 z-[400] group"
          aria-label="Open AI Query Interface"
        >
          <Sparkles className="w-6 h-6" />
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Ask AI about your data
          </span>
        </button>

        {/* Query Interface Modal */}
        <QueryInterface
          isOpen={isQueryInterfaceOpen}
          onClose={() => setIsQueryInterfaceOpen(false)}
          onApplyFilters={handleApplyFilters}
          onSwitchView={handleSwitchView}
        />
      </div>
    </div>
  )
}

export default Dashboard
